import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import SEO from '@/components/SEO';
import {
  ComposableMap,
  Geographies,
  Geography,
} from 'react-simple-maps';
import { geoMercator } from 'd3-geo';
import { Shield, Globe, AlertTriangle, ArrowLeft } from 'lucide-react';
import { IS_PRERENDER } from '@/lib/prerender';

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Country name to coordinates mapping
const countryCoordinates = {
  // By country name
  'United States': [-95.7129, 37.0902],
  'USA': [-95.7129, 37.0902],
  'China': [104.1954, 35.8617],
  'Russia': [105.3188, 61.524],
  'Germany': [10.4515, 51.1657],
  'France': [2.2137, 46.2276],
  'United Kingdom': [-3.436, 55.3781],
  'Brazil': [-51.9253, -14.235],
  'India': [78.9629, 20.5937],
  'Japan': [138.2529, 36.2048],
  'South Korea': [127.7669, 35.9078],
  'Australia': [133.7751, -25.2744],
  'Canada': [-106.3468, 56.1304],
  'Italy': [12.5674, 41.8719],
  'Spain': [-3.7492, 40.4637],
  'Netherlands': [5.2913, 52.1326],
  'The Netherlands': [5.2913, 52.1326],
  'Poland': [19.1451, 51.9194],
  'Ukraine': [31.1656, 48.3794],
  'Turkey': [35.2433, 38.9637],
  'Türkiye': [35.2433, 38.9637],
  'Indonesia': [113.9213, -0.7893],
  'Thailand': [100.9925, 15.87],
  'Vietnam': [108.2772, 14.0583],
  'Philippines': [121.774, 12.8797],
  'Malaysia': [101.9758, 4.2105],
  'Singapore': [103.8198, 1.3521],
  'Hong Kong': [114.1694, 22.3193],
  'Taiwan': [120.9605, 23.6978],
  'Pakistan': [69.3451, 30.3753],
  'Bangladesh': [90.3563, 23.685],
  'Iran': [53.688, 32.4279],
  'Saudi Arabia': [45.0792, 23.8859],
  'UAE': [53.8478, 23.4241],
  'Egypt': [30.8025, 26.8206],
  'South Africa': [22.9375, -30.5595],
  'Nigeria': [8.6753, 9.082],
  'Kenya': [37.9062, -0.0236],
  'Argentina': [-63.6167, -38.4161],
  'Chile': [-71.543, -35.6751],
  'Colombia': [-74.2973, 4.5709],
  'Mexico': [-102.5528, 23.6345],
  'Venezuela': [-66.5897, 6.4238],
  'Peru': [-75.0152, -9.19],
  'Romania': [24.9668, 45.9432],
  'Bulgaria': [25.4858, 42.7339],
  'Hungary': [19.5033, 47.1625],
  'Czechia': [15.473, 49.8175],
  'Czech Republic': [15.473, 49.8175],
  'Austria': [14.5501, 47.5162],
  'Sweden': [18.6435, 60.1282],
  'Norway': [8.4689, 60.472],
  'Finland': [25.7482, 61.9241],
  'Denmark': [9.5018, 56.2639],
  'Belgium': [4.4699, 50.5039],
  'Switzerland': [8.2275, 46.8182],
  'Portugal': [-8.2245, 39.3999],
  'Greece': [21.8243, 39.0742],
  'Ireland': [-8.2439, 53.4129],
  'New Zealand': [174.886, -40.9006],
  'Costa Rica': [-83.7534, 9.7489],
  'Zimbabwe': [29.1549, -19.0154],
  'DEFAULT': [0, 0],
};

// Attack type colors (API now returns English keys)
const attackTypeColors = {
  'Brute Force': '#ef4444',
  'Bruteforce': '#ef4444',
  'DDoS': '#f97316',
  'Web Scan': '#eab308',
  'Network Scan': '#22c55e',
  'Other': '#8b5cf6',
};

// Attack type translations
const attackTypeTranslations = {
  'Brute Force': { tr: 'Brute Force', en: 'Brute Force' },
  'Bruteforce': { tr: 'Bruteforce', en: 'Bruteforce' },
  'DDoS': { tr: 'DDoS', en: 'DDoS' },
  'Web Scan': { tr: 'Web Tarama', en: 'Web Scan' },
  'Network Scan': { tr: 'Ağ Tarama', en: 'Network Scan' },
  'Other': { tr: 'Diğer', en: 'Other' },
};

const ThreatMapPage = () => {
  const { language } = useLanguage();
  const [attacks, setAttacks] = useState([]);
  const [animatedAttacks, setAnimatedAttacks] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    byCountry: {},
    byType: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const translations = {
    tr: {
      title: 'Canlı Tehdit Haritası',
      subtitle: 'Gerçek zamanlı siber saldırı görselleştirmesi',
      totalAttacks: 'Toplam Saldırı',
      topAttackers: 'En Çok Saldıran Ülkeler',
      topTargets: 'En Çok Hedeflenen Ülkeler',
      attackTypes: 'Saldırı Tipleri',
      liveAttacks: 'Canlı Saldırılar',
      lastUpdate: 'Son Güncelleme',
      loading: 'Yükleniyor...',
      attackFrom: 'Saldırı:',
      bruteforce: 'Bruteforce',
      ddos: 'DDoS',
      webScan: 'Web Tarama',
      netScan: 'Ağ Tarama',
      other: 'Diğer',
      secondsAgo: 'saniye önce',
      minutesAgo: 'dakika önce',
      justNow: 'Az önce',
      from: 'Kaynak',
      to: 'Hedef',
      backHome: 'Ana Sayfa',
    },
    en: {
      title: 'Live Threat Map',
      subtitle: 'Real-time cyber attack visualization',
      totalAttacks: 'Total Attacks',
      topAttackers: 'Top Attacking Countries',
      topTargets: 'Top Targeted Countries',
      attackTypes: 'Attack Types',
      liveAttacks: 'Live Attacks',
      lastUpdate: 'Last Update',
      loading: 'Loading...',
      attackFrom: 'Attack:',
      bruteforce: 'Bruteforce',
      ddos: 'DDoS',
      webScan: 'Web Scan',
      netScan: 'Network Scan',
      other: 'Other',
      secondsAgo: 'seconds ago',
      minutesAgo: 'minutes ago',
      justNow: 'Just now',
      from: 'Source',
      to: 'Target',
      backHome: 'Home',
    }
  };

  const txt = translations[language] || translations.en;

  // Direct projection for SVG lines (avoids great-circle wrapping around the globe)
  const mapProjection = useMemo(() => {
    return geoMercator()
      .scale(180)
      .center([30, 25])
      .translate([400, 300]);
  }, []);

  // Translate attack type based on current language
  const translateAttackType = (type) => {
    return attackTypeTranslations[type]?.[language] || type;
  };

  // Fetch attacks from API
  const fetchAttacks = useCallback(async () => {
    if (IS_PRERENDER) return;
    try {
      const response = await fetch('https://api.threat.live/map');
      const data = await response.json();
      
      // API returns records with newest at the bottom — reverse for newest-first
      const rawAttacks = Array.isArray(data) ? data : (data.data || data.results || []);
      const recentAttacks = [...rawAttacks].reverse();
      
      // Deduplicate by IP for cleaner stats and display
      const seenIps = new Set();
      const uniqueAttacks = recentAttacks.filter(a => {
        if (seenIps.has(a.ip)) return false;
        seenIps.add(a.ip);
        return true;
      });
      
      setAttacks(uniqueAttacks);
      
      // Calculate stats from unique attacks
      const bySourceCountry = {};
      const byTargetCountry = {};
      const byType = {};
      
      uniqueAttacks.forEach(attack => {
        // Count by source country
        const source = attack.source_country || 'Unknown';
        bySourceCountry[source] = (bySourceCountry[source] || 0) + 1;
        
        // Count by target country
        const target = attack.target_country || 'Unknown';
        byTargetCountry[target] = (byTargetCountry[target] || 0) + 1;
        
        // Count by type
        const type = attack.type || 'Other';
        byType[type] = (byType[type] || 0) + 1;
      });
      
      setStats({
        total: uniqueAttacks.length,
        bySourceCountry,
        byTargetCountry,
        byType,
      });
      
      setLastUpdate(new Date());
      setIsLoading(false);
      
      // Trigger new animations for recent unique attacks
      const newAnimations = uniqueAttacks.slice(0, 15).map((attack, index) => ({
        ...attack,
        id: `${attack.ip}-${Date.now()}-${index}`,
        startTime: Date.now() + (index * 400), // Stagger animations
      }));
      
      setAnimatedAttacks(prev => [...newAnimations, ...prev].slice(0, 40));
      
    } catch (error) {
      console.error('Failed to fetch attacks:', error);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchAttacks();
    const interval = setInterval(fetchAttacks, 8000); // Poll every 8 seconds
    return () => clearInterval(interval);
  }, [fetchAttacks]);

  // Clean up old animations
  useEffect(() => {
    const cleanup = setInterval(() => {
      const now = Date.now();
      setAnimatedAttacks(prev => 
        prev.filter(a => now - a.startTime < 5000)
      );
    }, 1000);
    return () => clearInterval(cleanup);
  }, []);

  // Get coordinates for a country name
  const getCoordinates = (countryName) => {
    return countryCoordinates[countryName] || countryCoordinates.DEFAULT;
  };

  // Get top source countries sorted by attack count
  const topSourceCountries = useMemo(() => {
    return Object.entries(stats.bySourceCountry || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
  }, [stats.bySourceCountry]);

  // Get top target countries sorted by attack count
  const topTargetCountries = useMemo(() => {
    return Object.entries(stats.byTargetCountry || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
  }, [stats.byTargetCountry]);

  // Get attack types sorted
  const attackTypes = useMemo(() => {
    return Object.entries(stats.byType || {})
      .sort((a, b) => b[1] - a[1]);
  }, [stats.byType]);

  // Format time ago (API returns UTC timestamps)
  const formatTimeAgo = (dateString) => {
    let parsed = dateString;
    if (parsed && !parsed.endsWith('Z') && !parsed.includes('+')) {
      parsed = parsed.replace(' ', 'T');
      if (!parsed.endsWith('Z')) parsed += 'Z';
    }
    const date = new Date(parsed);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    
    if (diffMin > 60) return `${Math.floor(diffMin / 60)}h`;
    if (diffMin > 0) return `${diffMin}m`;
    if (diffSec > 10) return `${diffSec}s`;
    return txt.justNow;
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <SEO page="map" />
      
      <main className="relative">
        {/* Map Container - Full screen */}
        <div className="relative w-full h-screen overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 via-zinc-950 to-black"></div>
          
          {/* Grid overlay */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                linear-gradient(rgba(16, 185, 129, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(16, 185, 129, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px',
            }}
          ></div>
          
          {/* World Map */}
          <div className="absolute inset-0 flex items-center justify-center">
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{
                scale: 180,
                center: [30, 25],
              }}
              style={{
                width: '100%',
                height: '100%',
              }}
            >
              <Geographies geography={geoUrl}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill="#1f2937"
                      stroke="#374151"
                      strokeWidth={0.5}
                      style={{
                        default: { outline: 'none' },
                        hover: { fill: '#374151', outline: 'none' },
                        pressed: { outline: 'none' },
                      }}
                    />
                  ))
                }
              </Geographies>
              
              {/* Attack Lines - Curved arcs via projected SVG bezier */}
              {animatedAttacks.map((attack) => {
                const sourceCoords = getCoordinates(attack.source_country);
                const targetCoords = getCoordinates(attack.target_country);
                const progress = Math.min(1, (Date.now() - attack.startTime) / 2500);
                const color = attackTypeColors[attack.type] || '#8b5cf6';
                
                if (progress < 0) return null;
                
                // Skip if coordinates are not found
                if (sourceCoords[0] === 0 && sourceCoords[1] === 0) return null;
                if (targetCoords[0] === 0 && targetCoords[1] === 0) return null;
                
                const from = mapProjection(sourceCoords);
                const to = mapProjection(targetCoords);
                if (!from || !to) return null;
                
                // Curved arc: control point elevated above midpoint
                const dx = to[0] - from[0];
                const dy = to[1] - from[1];
                const dist = Math.sqrt(dx * dx + dy * dy);
                const midX = (from[0] + to[0]) / 2;
                const midY = (from[1] + to[1]) / 2;
                const arcHeight = Math.max(dist * 0.35, 20);
                const pathD = `M ${from[0]},${from[1]} Q ${midX},${midY - arcHeight} ${to[0]},${to[1]}`;
                
                return (
                  <g key={attack.id}>
                    {/* Curved attack arc */}
                    <path
                      d={pathD}
                      fill="none"
                      stroke={color}
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      opacity={0.5 * (1 - progress * 0.5)}
                      style={{
                        filter: `drop-shadow(0 0 4px ${color})`,
                      }}
                    />
                    
                    {/* Traveling dot along the arc */}
                    <circle
                      cx={from[0]}
                      cy={from[1]}
                      r={3}
                      fill={color}
                      opacity={0.95}
                      style={{
                        filter: `drop-shadow(0 0 8px ${color}) drop-shadow(0 0 3px #fff)`,
                      }}
                    >
                      <animateMotion
                        dur="1.8s"
                        repeatCount="indefinite"
                        path={`M 0,0 Q ${midX - from[0]},${midY - arcHeight - from[1]} ${to[0] - from[0]},${to[1] - from[1]}`}
                      />
                    </circle>
                    
                    {/* Source dot */}
                    <circle
                      cx={from[0]}
                      cy={from[1]}
                      r={3}
                      fill={color}
                      opacity={0.6 * (1 - progress * 0.3)}
                      style={{
                        filter: `drop-shadow(0 0 4px ${color})`,
                      }}
                    />
                    
                    {/* Target dot */}
                    <circle
                      cx={to[0]}
                      cy={to[1]}
                      r={4}
                      fill="#10b981"
                      opacity={0.6 * (1 - progress * 0.3)}
                      style={{
                        filter: `drop-shadow(0 0 6px #10b981)`,
                      }}
                    />
                  </g>
                );
              })}
            </ComposableMap>
          </div>

          {/* Title Overlay + Back Button */}
          <div className="absolute top-4 left-4 z-10">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 px-4 py-2 mb-3 text-sm font-medium text-white bg-zinc-800/80 backdrop-blur-md rounded-lg border border-zinc-600/50 hover:bg-zinc-700/80 transition-colors"
              data-testid="map-back-home"
            >
              <ArrowLeft className="h-4 w-4" />
              {txt.backHome}
            </Link>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg backdrop-blur-sm border border-emerald-500/30">
                <Globe className="h-6 w-6 text-emerald-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white" style={{ textShadow: '0 0 7px rgba(16, 185, 129, 0.8), 0 0 20px rgba(16, 185, 129, 0.5), 0 0 40px rgba(16, 185, 129, 0.2)' }}>{txt.title}</h1>
                <p className="text-sm text-emerald-400/80" style={{ textShadow: '0 0 7px rgba(16, 185, 129, 0.5), 0 0 15px rgba(16, 185, 129, 0.3)' }}>{txt.subtitle}</p>
              </div>
            </div>
          </div>

          {/* Stats Panel - Top Right */}
          <div className="absolute top-4 right-4 z-10 w-72 hidden md:block">
            {/* Top Attacking Countries */}
            <div className="bg-zinc-900/80 backdrop-blur-md rounded-lg border border-zinc-800 p-4 mb-3">
              <div className="flex items-center justify-between mb-3">
                <span className="text-zinc-400 text-sm">{txt.topAttackers}</span>
                <AlertTriangle className="h-4 w-4 text-red-400" />
              </div>
              <div className="space-y-2">
                {topSourceCountries.map(([country, count], index) => (
                  <div key={country} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500 w-4">{index + 1}.</span>
                      <span className="text-sm text-zinc-300 truncate max-w-[100px]">{country}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-12 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-full"
                          style={{ width: `${(count / (topSourceCountries[0]?.[1] || 1)) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-zinc-400 w-6 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Targeted Countries */}
            <div className="bg-zinc-900/80 backdrop-blur-md rounded-lg border border-zinc-800 p-4 mb-3">
              <div className="flex items-center justify-between mb-3">
                <span className="text-zinc-400 text-sm">{txt.topTargets}</span>
                <Shield className="h-4 w-4 text-emerald-400" />
              </div>
              <div className="space-y-2">
                {topTargetCountries.map(([country, count], index) => (
                  <div key={country} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500 w-4">{index + 1}.</span>
                      <span className="text-sm text-zinc-300 truncate max-w-[100px]">{country}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-12 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full"
                          style={{ width: `${(count / (topTargetCountries[0]?.[1] || 1)) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-zinc-400 w-6 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Live Attack Feed - Desktop: Bottom Left compact list */}
          <div className="absolute bottom-4 left-4 z-10 w-80 hidden md:block" data-testid="live-attacks-panel">
            <div className="bg-zinc-900/80 backdrop-blur-md rounded-lg border border-zinc-800 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-zinc-400 text-xs font-medium">{txt.liveAttacks}</span>
                </div>
              </div>
              
              <div className="space-y-0.5 max-h-[220px] overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-700">
                {attacks.slice(0, 15).map((attack, index) => (
                  <div 
                    key={`${attack.ip}-${index}`}
                    className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-zinc-800/50 transition-colors group"
                  >
                    <div 
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: attackTypeColors[attack.type] || '#8b5cf6' }}
                    ></div>
                    <span className="text-[11px] font-mono text-zinc-300 w-[110px] truncate">{attack.ip}</span>
                    <span className="text-[10px] text-red-400 w-[52px] truncate">{attack.source_country}</span>
                    <span className="text-[10px] text-zinc-600">→</span>
                    <span className="text-[10px] text-emerald-400 w-[52px] truncate">{attack.target_country}</span>
                    <span className="text-[10px] text-zinc-600 ml-auto">{formatTimeAgo(attack.datetime)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Live Attack Feed - Mobile: Bottom compact list */}
          <div className="absolute bottom-2 left-2 right-2 z-10 md:hidden" data-testid="live-attacks-panel-mobile">
            <div className="bg-zinc-900/80 backdrop-blur-md rounded-lg border border-zinc-800 p-2.5">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-zinc-400 text-[10px] font-medium">{txt.liveAttacks}</span>
              </div>
              <div className="space-y-0.5 max-h-[100px] overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-700">
                {attacks.slice(0, 8).map((attack, index) => (
                  <div 
                    key={`mobile-${attack.ip}-${index}`}
                    className="flex items-center gap-1.5 py-1 px-1.5 rounded"
                  >
                    <div 
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: attackTypeColors[attack.type] || '#8b5cf6' }}
                    ></div>
                    <span className="text-[10px] font-mono text-zinc-300 truncate flex-1">{attack.ip}</span>
                    <span className="text-[9px] text-red-400 truncate max-w-[50px]">{attack.source_country}</span>
                    <span className="text-[9px] text-zinc-600">→</span>
                    <span className="text-[9px] text-emerald-400 truncate max-w-[50px]">{attack.target_country}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="absolute bottom-4 right-4 z-10 hidden md:block">
            <div className="bg-zinc-900/80 backdrop-blur-md rounded-lg border border-zinc-800 p-3">
              <div className="text-xs text-zinc-500 mb-2">Legend</div>
              <div className="space-y-1">
                {[
                  ['Brute Force', '#ef4444'],
                  ['DDoS', '#f97316'],
                  ['Web Scan', '#eab308'],
                  ['Network Scan', '#22c55e'],
                  ['Other', '#8b5cf6'],
                ].map(([type, color]) => (
                  <div key={type} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-0.5 rounded-full"
                      style={{ backgroundColor: color, boxShadow: `0 0 6px ${color}` }}
                    ></div>
                    <span className="text-xs text-zinc-400">{translateAttackType(type)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ThreatMapPage;
